import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Renders a deliverable, with two additions: `[F12]` citations become links to
 * the finding in the evidence tab, and a link to a sibling deliverable is
 * resolved to that file's URL.
 *
 * Tracing a claim to its source is the whole point of the ledger, so it should
 * be one click, not a text search. The same goes for a deliverable that
 * references the deliverables beside it — `cim_review.html` is written
 * relative to the workspace the files share, and read here it would otherwise
 * resolve against the app's own route and land on the SPA's catch-all.
 */
export function Markdown({
  content,
  onCitationClick,
  resolveHref,
  citationPrefix = "F",
}: {
  content: string;
  onCitationClick?: (id: number) => void;
  resolveHref?: (href: string) => string;
  /** `F` for a run's findings, `M` for a memory briefing's items. */
  citationPrefix?: string;
}) {
  const cite = (children: React.ReactNode) =>
    withCitations(children, onCitationClick, citationPrefix);
  return (
    <div className="prose-doc">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p>{cite(children)}</p>,
          li: ({ children }) => <li>{cite(children)}</li>,
          td: ({ children }) => <td>{cite(children)}</td>,
          a: ({ href, children }) => (
            <a
              href={href && resolveHref ? resolveHref(href) : href}
              target="_blank"
              rel="noreferrer noopener"
            >
              {children}
            </a>
          ),
          pre: ({ children }) => {
            const spec = chartSpec(children);
            return spec ? <ChartTable spec={spec} /> : <pre>{children}</pre>;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

/**
 * A ```chart fence carries a chart's data as JSON. The interactive version is
 * one tab away in the HTML deliverable; here the figures are rendered as a
 * table, which is what a reader looking something up wants anyway — and is a
 * great deal better than a wall of JSON, which is what this used to show.
 */
interface ChartSpec {
  type?: string;
  title?: string;
  source?: string;
  unit?: string;
  x_label?: string;
  y_label?: string;
  categories?: unknown[];
  series?: { name?: string; data?: unknown[] }[];
  points?: { x?: unknown; y?: unknown; label?: string }[];
}

function chartSpec(children: React.ReactNode): ChartSpec | null {
  const child = Array.isArray(children) ? children[0] : children;
  const props = (child as { props?: { className?: string; children?: unknown } })
    ?.props;
  if (!props?.className?.includes("language-chart")) return null;
  try {
    const parsed: unknown = JSON.parse(String(props.children ?? ""));
    return parsed && typeof parsed === "object" ? (parsed as ChartSpec) : null;
  } catch {
    return null;
  }
}

function cell(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function ChartTable({ spec }: { spec: ChartSpec }) {
  const isPoints = spec.type === "scatter";
  const columns = isPoints
    ? ["Label", spec.x_label ?? "X", spec.y_label ?? "Y"]
    : [spec.x_label ?? "", ...(spec.series ?? []).map((s) => s.name ?? "")];
  const rows = isPoints
    ? (spec.points ?? []).map((p) => [cell(p.label), cell(p.x), cell(p.y)])
    : (spec.categories ?? []).map((category, index) => [
        cell(category),
        ...(spec.series ?? []).map((s) => cell((s.data ?? [])[index])),
      ]);
  const footer = [
    spec.unit ? `Unit: ${spec.unit}` : "",
    spec.source ? `Source: ${spec.source}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <figure className="my-5 rounded-lg border border-line px-4 py-3">
      {spec.title ? (
        <figcaption className="mb-2 text-sm font-medium">{spec.title}</figcaption>
      ) : null}
      <div className="overflow-x-auto">
        <table>
          <thead>
            <tr>
              {columns.map((name, i) => (
                <th key={i}>{name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((value, j) => (
                  <td key={j}>{value}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer ? <p className="mt-2 text-xs text-muted">{footer}</p> : null}
    </figure>
  );
}

/** `[F3]`, `[F3, F7]` — or with another prefix, `[M3, M7]`. */
function citationPattern(prefix: string): RegExp {
  return new RegExp(`\\[${prefix}(\\d+(?:\\s*,\\s*${prefix}?\\d+)*)\\]`, "g");
}

function withCitations(
  children: React.ReactNode,
  onClick?: (id: number) => void,
  prefix = "F",
): React.ReactNode {
  if (!onClick) return children;
  const CITATION = citationPattern(prefix);

  const walk = (node: React.ReactNode, key: number): React.ReactNode => {
    if (typeof node !== "string") return node;

    const parts: React.ReactNode[] = [];
    let cursor = 0;
    let match: RegExpExecArray | null;
    CITATION.lastIndex = 0;

    while ((match = CITATION.exec(node)) !== null) {
      if (match.index > cursor) parts.push(node.slice(cursor, match.index));
      const ids = match[1]!
        .split(",")
        .map((raw) => Number.parseInt(raw.replace(/\D/g, ""), 10))
        .filter((n) => Number.isFinite(n));
      parts.push(
        <span key={`${key}-${match.index}`}>
          [
          {ids.map((id, i) => (
            <span key={id}>
              {i > 0 && ", "}
              <button
                onClick={() => onClick(id)}
                className="font-medium text-accent underline underline-offset-2"
                title={prefix === "F" ? `Show finding F${id}` : `Show ${prefix}${id}`}
              >
                {prefix}
                {id}
              </button>
            </span>
          ))}
          ]
        </span>,
      );
      cursor = match.index + match[0].length;
    }

    if (parts.length === 0) return node;
    if (cursor < node.length) parts.push(node.slice(cursor));
    return parts;
  };

  return Array.isArray(children)
    ? children.map((child, i) => walk(child, i))
    : walk(children, 0);
}
